import { withPlugins, inview } from '@weareduet/plugins';

/*
 * FadeIn — scroll entrances for `[data-fade-in]`.
 *
 * An element arrives from Twig with `data-fade-in`; foundations/animations.css
 * holds it faded and 24px low (only while JS runs and motion is allowed) and
 * reveals it when this global adds `.has-moved`. `data-fade-in="custom"` opts
 * out of those shared styles: the element is only told it is in view, for a
 * component with its own entrance (keyStats, imageTextFeature).
 *
 * STAGGER WITHOUT MARKUP. Elements that come into view in the same observer
 * batch — a row of cards, or everything above the fold on load — are
 * numbered in document order as `--fade-in-index`, which the CSS turns into
 * a delay (capped, so a long batch never keeps the last item waiting). Items
 * reached one at a time while scrolling get index 0 and arrive at once. So a
 * block needs no group wrapper: putting data-fade-in on each card is enough.
 *
 * Reveals once: a revealed node is unobserved and never re-hidden.
 */

const SELECTOR = '[data-fade-in]:not(.has-moved)';

// Reveal once 10% of the viewport above the bottom edge — close enough that
// the motion is seen, not so late that the element sits blank in view (the
// old -40% left the lower part of the screen empty until a scroll).
const ROOT_MARGIN = '0px 0px -10% 0px';

// Set while the global is mounted so `fadeInWithin` can reach the live
// observer. Nulled by the cleanup below, which makes the export a no-op.
let observeWithin = null;

/**
 * Observe the not-yet-revealed `[data-fade-in]` nodes inside `root`.
 *
 * `FadeIn` is a `keepAlive` global mounted once on `document.body`, so content
 * arriving through a Sprig/htmx swap is never picked up by the initial pass.
 * Swap handlers call this with the swapped target to hand the new nodes to the
 * same observer. No-op until the global has mounted.
 */
export function fadeInWithin(root) {
	if (typeof observeWithin === 'function' && root) observeWithin(root);
}

/** Document order, so a batch staggers in reading order. */
const byDocumentOrder = (a, b) =>
	a.compareDocumentPosition(b) & window.Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;

/**
 * Reveal a batch of elements that entered together, staggered in document
 * order. Exported for the tests; the CSS caps the delay.
 */
export function revealBatch(targets) {
	[...targets].sort(byDocumentOrder).forEach((target, index) => {
		target.style.setProperty('--fade-in-index', String(index));
		target.classList.add('has-moved');
	});
}

function FadeIn({ node, plugins: { inview } }) {
	// The @weareduet/plugins inview plugin reads `this.settings` inside
	// observe(); assigning to it is its only configuration API.
	inview.settings = { rootMargin: ROOT_MARGIN };

	// Entering targets are collected for the rest of the frame, then revealed
	// together: the plugin calls `enter` once per entry, so this is what turns
	// one observer callback into one staggered batch.
	let batch = [];
	let frame = null;
	const flush = () => {
		frame = null;
		const targets = batch;
		batch = [];
		revealBatch(targets);
	};

	const observe = root => {
		const targets = [
			...(root.matches?.(SELECTOR) ? [root] : []),
			...root.querySelectorAll(SELECTOR),
		];

		if (!targets.length) return;

		// The plugin mints a new IntersectionObserver per `observe()` call and
		// only empties its set on `disconnect()`, which happens when this
		// keepAlive global unmounts — i.e. never during the life of the page.
		// Unobserving on reveal stops each swap's observer retaining its (soon
		// detached) nodes. Residual: nodes swapped away before ever
		// intersecting stay referenced until unload.
		inview.observe(targets, {
			enter: ({ target }, observer) => {
				observer.unobserve(target);
				batch.push(target);
				if (frame === null) frame = requestAnimationFrame(flush);
			},
		});
	};

	observeWithin = observe;
	observe(node);
	// Tells the CSS the global is live, which retires its no-script failsafe.
	document.documentElement.classList.add('fade-in-ready');

	return () => {
		observeWithin = null;
		if (frame !== null) cancelAnimationFrame(frame);
		inview.disconnect();
	};
}
export default withPlugins(inview)(FadeIn);
